import React, { useState, useEffect, useCallback } from "react";
import { PageHeader, Card, Tag } from "../components/UI";
import api, { formatError } from "../lib/api";
import { Loader2, Save, RotateCcw, MessageSquare, CheckCircle2, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";

function TemplateRow({ t, onSaved }) {
  const [body, setBody] = useState(t.body);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { setBody(t.body); }, [t.body]);
  const dirty = body !== t.body;

  async function save() {
    setSaving(true); setErr(""); setMsg("");
    try {
      await api.put(`/whatsapp/templates/${t.key}`, { body });
      setMsg("Saved"); onSaved();
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setErr(formatError(e)); }
    finally { setSaving(false); }
  }

  async function reset() {
    setSaving(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post(`/whatsapp/templates/${t.key}/reset`);
      setBody(data.body); setMsg("Reset to default"); onSaved();
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setErr(formatError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="surface overflow-hidden" data-testid={`wa-template-${t.key}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-slate-50 transition-colors"
        data-testid={`wa-template-toggle-${t.key}`}
      >
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
        <MessageSquare size={16} className="text-teal-700" />
        <span className="font-medium text-slate-800 text-[13.5px]">{t.title}</span>
        <code className="text-[11px] text-slate-400">{t.key}</code>
        <div className="ml-auto flex items-center gap-2">
          {dirty && <Tag tone="amber">Unsaved</Tag>}
          {t.customised ? <Tag tone="teal">Customised</Tag> : <Tag tone="slate">Default</Tag>}
        </div>
      </button>

      {open && (
        <div className="p-4 pt-1 border-t border-slate-100 space-y-3">
          {t.placeholders?.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="small-caps text-slate-500 mr-1">Placeholders</span>
              {t.placeholders.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setBody((b) => b + `{{${p}}}`)}
                  className="text-[11px] font-mono bg-teal-50 text-teal-700 border border-teal-200 rounded px-1.5 py-0.5 hover:bg-teal-100"
                  title="Click to insert"
                >{`{{${p}}}`}</button>
              ))}
              <span className="text-[11px] text-slate-400 ml-1">· {`{{SIGN}}`} and {`{{TAG}}`} are always available</span>
            </div>
          )}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={Math.min(20, Math.max(6, body.split("\n").length + 1))}
            className="w-full font-mono !text-[12.5px] leading-relaxed"
            data-testid={`wa-template-input-${t.key}`}
          />
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving || !dirty} className="btn-primary !py-1.5 inline-flex items-center gap-1.5 disabled:opacity-40" data-testid={`wa-template-save-${t.key}`}>
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
            <button onClick={reset} disabled={saving || !t.customised} className="btn-secondary !py-1.5 inline-flex items-center gap-1.5 disabled:opacity-40" data-testid={`wa-template-reset-${t.key}`}>
              <RotateCcw size={13} /> Reset to default
            </button>
            {msg && <span className="text-[12px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={13} /> {msg}</span>}
            {err && <span className="text-[12px] text-rose-700 inline-flex items-center gap-1"><AlertCircle size={13} /> {err}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WhatsAppMessages() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/whatsapp/templates");
      setTemplates(data || []);
    } catch (e) { setError(formatError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="wa-messages">
      <PageHeader
        kicker="Engagement · WhatsApp"
        title="WhatsApp Journey Messages"
        subtitle="Edit every message the CBC WhatsApp bot sends — menus, points, redeem journey, update address and more. Changes go live instantly (no redeploy). Use {{placeholders}} for dynamic values."
        testId="wa-messages-header"
      />

      {error && <Card><div className="text-[12px] text-rose-700 inline-flex items-center gap-1"><AlertCircle size={13} /> {error}</div></Card>}

      {loading ? (
        <Card><div className="py-10 text-center text-slate-400 inline-flex items-center gap-2 justify-center w-full"><Loader2 size={16} className="animate-spin" /> Loading messages…</div></Card>
      ) : (
        <div className="space-y-2.5">
          {templates.map((t) => <TemplateRow key={t.key} t={t} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
